import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useAutoSave } from '../hooks/useAutoSave';
import { schoolAPI } from '../services/api';
import DeleteIconButton from './DeleteIconButton';
import './SchoolForm.css';

const emptyContact = {
  contact_type_id: '',
  first_name: '',
  last_name: '',
  title: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  fax: '',
  cell: '',
  email: '',
  comment: '',
  is_primary: false,
};

function SchoolForm() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const pathname = location.pathname || '';
  /** Same pattern as ContactForm: /client/:id/school/:schoolId keeps ClientLayout + main nav. */
  const isNestedUnderClient = pathname.includes('/client/') && pathname.includes('/school/');
  const schoolDistrictId = isNestedUnderClient ? params.schoolId : params.id;
  const clientIdParam = isNestedUnderClient ? params.id : null;
  const isEdit = !!schoolDistrictId && schoolDistrictId !== 'new';

  const backTo = clientIdParam ? tenantPath(`/client/${clientIdParam}`) : tenantPath('/lists/schools');

  const [formData, setFormData] = useState({
    district: '',
    district_name: '',
    school_year_start: '',
    website: '',
    comments: '',
  });

  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [canDeleteContact, setCanDeleteContact] = useState(false);
  const [contactTypes, setContactTypes] = useState([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactForm, setContactForm] = useState(emptyContact);
  const [contactSaving, setContactSaving] = useState(false);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [showContactsHint, setShowContactsHint] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadSchool();
    }
  }, [isEdit, schoolDistrictId]);

  useEffect(() => {
    if (isEdit && schoolDistrictId) {
      loadContacts();
      schoolAPI.getContactTypes().then((r) => setContactTypes(r.data?.data ?? [])).catch(() => setContactTypes([]));
    }
  }, [isEdit, schoolDistrictId]);

  const loadSchool = async () => {
    try {
      setLoading(true);
      const response = await schoolAPI.getById(schoolDistrictId);
      const school = response.data.data;
      setFormData({
        district: school.district || '',
        district_name: school.district_name || '',
        school_year_start: school.school_year_start ? school.school_year_start.split('T')[0] : '',
        website: school.website || '',
        comments: school.comments || '',
      });
    } catch (error) {
      console.error('Error loading school district:', error);
      alert('Failed to load school district');
      navigate(backTo);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    if (!schoolDistrictId) return;
    try {
      const r = await schoolAPI.getContacts(schoolDistrictId);
      setContacts(r.data?.data ?? []);
      setCanDeleteContact(r.data?.canDeleteContacts === true);
    } catch {
      setContacts([]);
      setCanDeleteContact(false);
    }
  };

  const openAddContact = () => {
    setEditingContactId(null);
    setContactForm(emptyContact);
    setShowContactForm(true);
  };

  const openEditContact = (c) => {
    setEditingContactId(c.id);
    setContactForm({
      contact_type_id: c.contact_type_id,
      first_name: c.first_name ?? '',
      last_name: c.last_name ?? '',
      title: c.title ?? '',
      street1: c.street1 ?? '',
      street2: c.street2 ?? '',
      city: c.city ?? '',
      state: c.state ?? '',
      zip: c.zip ?? '',
      phone: c.phone ?? '',
      fax: c.fax ?? '',
      cell: c.cell ?? '',
      email: c.email ?? '',
      comment: c.comment ?? '',
      is_primary: c.is_primary === true,
    });
    setShowContactForm(true);
  };

  const closeContactForm = () => {
    setShowContactForm(false);
    setEditingContactId(null);
    setContactForm(emptyContact);
  };

  const handleContactChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    const first = (contactForm.first_name ?? '').trim();
    const last = (contactForm.last_name ?? '').trim();
    const phone = (contactForm.phone ?? '').trim();
    const email = (contactForm.email ?? '').trim();
    if (!first || !last) {
      alert('First name and last name are required.');
      return;
    }
    if (!phone && !email) {
      alert('At least one of phone or email is required.');
      return;
    }
    if (!contactForm.contact_type_id) {
      alert('Contact type is required.');
      return;
    }
    setContactSaving(true);
    try {
      const payload = {
        contact_type_id: contactForm.contact_type_id ? String(contactForm.contact_type_id) : null,
        first_name: (contactForm.first_name ?? '').trim(),
        last_name: (contactForm.last_name ?? '').trim(),
        title: (contactForm.title ?? '').trim() || null,
        street1: (contactForm.street1 ?? '').trim() || null,
        street2: (contactForm.street2 ?? '').trim() || null,
        city: (contactForm.city ?? '').trim() || null,
        state: (contactForm.state ?? '').trim() || null,
        zip: (contactForm.zip ?? '').trim() || null,
        phone: (contactForm.phone ?? '').trim() || null,
        fax: (contactForm.fax ?? '').trim() || null,
        cell: (contactForm.cell ?? '').trim() || null,
        email: (contactForm.email ?? '').trim() || null,
        comment: (contactForm.comment ?? '').trim() || null,
        is_primary: contactForm.is_primary === true,
      };
      if (editingContactId) {
        await schoolAPI.updateContact(schoolDistrictId, editingContactId, payload);
      } else {
        await schoolAPI.createContact(schoolDistrictId, payload);
      }
      await loadContacts();
      closeContactForm();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save contact';
      alert(msg);
      console.error('Save contact failed:', err.response?.data || err);
    } finally {
      setContactSaving(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await schoolAPI.deleteContact(schoolDistrictId, contactId);
      await loadContacts();
      if (editingContactId === contactId) closeContactForm();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (isEdit) setSaveTrigger((p) => p + 1);
  };

  const performSave = useCallback(async () => {
    if (isEdit) {
      await schoolAPI.update(schoolDistrictId, formData);
    } else {
      await schoolAPI.create(formData);
    }
  }, [formData, isEdit, schoolDistrictId]);

  useAutoSave(saveTrigger, performSave, { enabled: isEdit, delayMs: 500 });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      await performSave();
      navigate(backTo);
    } catch (error) {
      console.error('Error saving school district:', error);
      alert('Failed to save school district');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return <div className="loading">Loading school district...</div>;
  }

  return (
    <div className="school-form">
      <div className="school-form-header">
        <div className="school-form-header-left">
          <h1>{isEdit ? 'Edit School District' : 'Add School District'}</h1>
        </div>
        <div className="school-form-header-actions">
          {!isEdit && (
            <>
              <button
                type="button"
                onClick={() => document.getElementById('school-form-main').requestSubmit()}
                disabled={loading}
                className="btn-save"
              >
                {loading ? 'Saving…' : 'Create school district'}
              </button>
              <Link to={backTo} className="btn-cancel">
                Cancel
              </Link>
            </>
          )}
        </div>
      </div>

      <form id="school-form-main" onSubmit={handleSubmit}>
        <div className="form-row form-row-three">
          <div className="form-group">
            <label>District Code *</label>
            <input
              type="text"
              name="district"
              value={formData.district}
              onChange={handleChange}
              required
              maxLength="50"
              placeholder="129 or U-46"
            />
            <small>Short alphanumeric code (e.g., "129" or "U-46")</small>
          </div>
          <div className="form-group">
            <label>District Name *</label>
            <input
              type="text"
              name="district_name"
              value={formData.district_name}
              onChange={handleChange}
              required
              maxLength="255"
              placeholder="Huntley School District"
            />
          </div>
          <div className="form-group">
            <label>School Year Start</label>
            <input
              type="date"
              name="school_year_start"
              value={formData.school_year_start}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row form-row-three">
          <div className="form-group">
            <label>Website</label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://www.example.org"
            />
          </div>
          <div className="form-group form-group-span-2">
            <label>Comments</label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows="3"
              placeholder="Additional notes about this school district..."
            />
          </div>
        </div>

        <div className="form-actions">
          {!isEdit && (
            <>
              <Link to={backTo} className="btn-cancel">
                Cancel
              </Link>
              <button type="submit" disabled={loading} className="btn-save">
                {loading ? 'Saving…' : 'Create school district'}
              </button>
            </>
          )}
        </div>
      </form>

      {isEdit && (
          <div className="school-contacts-section">
            <div className="school-contacts-section-header">
              <h2 className="school-contacts-section-title">District Contacts</h2>
              <button
                type="button"
                className="school-contacts-info-icon"
                onClick={() => setShowContactsHint((v) => !v)}
                aria-expanded={showContactsHint}
                aria-controls="school-contacts-hint"
                aria-label={showContactsHint ? 'Hide District Contacts info' : 'Show District Contacts info'}
              >
                <span aria-hidden>ⓘ</span>
              </button>
            </div>
            {showContactsHint && (
              <p id="school-contacts-hint" className="school-contacts-hint">
                Add contacts by type (Referral, TPC, Summer, etc.). At least first name, last name, and one of phone or email required.
              </p>
            )}
            <div className="school-contacts-toolbar">
              <button type="button" onClick={openAddContact} className="btn-add-contact">
                + Add Contact
              </button>
            </div>
            {contacts.length > 0 && (
              <div className="school-contacts-table-wrap">
                <table className="school-contacts-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Primary</th>
                      {canDeleteContact && <th className="col-actions">Delete</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => openEditContact(c)}
                        className="school-contacts-row-clickable"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditContact(c); } }}
                        title="Click to edit"
                      >
                        <td>{c.contact_type_description || c.contact_type_code}</td>
                        <td>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                        <td>{c.phone || c.cell || '—'}</td>
                        <td>{c.email || '—'}</td>
                        <td className="school-contacts-primary-cell">
                            <input type="checkbox" checked={c.is_primary === true} readOnly disabled aria-label={c.is_primary ? 'Primary' : 'Not primary'} />
                          </td>
                        {canDeleteContact && (
                          <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                            <DeleteIconButton className="list-btn-delete" onClick={() => handleDeleteContact(c.id)} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showContactForm && (
              <div className="school-contact-form-wrap">
                <h3>{editingContactId ? 'Edit Contact' : 'Add Contact'}</h3>
                <form onSubmit={handleContactSubmit} className="school-contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Contact type *</label>
                      <select name="contact_type_id" value={contactForm.contact_type_id} onChange={handleContactChange} required>
                        <option value="">Select type</option>
                        {contactTypes.map((t) => (
                          <option key={t.id} value={t.id}>{t.description || t.code}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>First name *</label>
                      <input type="text" name="first_name" value={contactForm.first_name} onChange={handleContactChange} required />
                    </div>
                    <div className="form-group">
                      <label>Last name *</label>
                      <input type="text" name="last_name" value={contactForm.last_name} onChange={handleContactChange} required />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Title</label>
                      <input type="text" name="title" value={contactForm.title} onChange={handleContactChange} />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input type="text" name="phone" value={contactForm.phone} onChange={handleContactChange} />
                    </div>
                    <div className="form-group">
                      <label>Email</label>
                      <input type="email" name="email" value={contactForm.email} onChange={handleContactChange} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Fax</label>
                      <input type="text" name="fax" value={contactForm.fax} onChange={handleContactChange} />
                    </div>
                    <div className="form-group">
                      <label>Cell</label>
                      <input type="text" name="cell" value={contactForm.cell} onChange={handleContactChange} />
                    </div>
                  </div>
                  <div className="form-row form-row-single">
                    <div className="form-group form-group-check">
                      <label className="school-contact-primary-label">
                        <input type="checkbox" name="is_primary" checked={contactForm.is_primary} onChange={handleContactChange} />
                        <span>Primary for this type</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Street 1</label>
                      <input type="text" name="street1" value={contactForm.street1} onChange={handleContactChange} />
                    </div>
                    <div className="form-group">
                      <label>Street 2</label>
                      <input type="text" name="street2" value={contactForm.street2} onChange={handleContactChange} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input type="text" name="city" value={contactForm.city} onChange={handleContactChange} />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input type="text" name="state" value={contactForm.state} onChange={handleContactChange} />
                    </div>
                    <div className="form-group">
                      <label>Zip</label>
                      <input type="text" name="zip" value={contactForm.zip} onChange={handleContactChange} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Comment</label>
                    <textarea name="comment" value={contactForm.comment} onChange={handleContactChange} rows={2} />
                  </div>
                  <div className="form-actions school-contact-form-actions">
                    <button type="submit" className="btn-primary" disabled={contactSaving}>{contactSaving ? 'Saving…' : 'Save contact'}</button>
                    <button type="button" className="btn-cancel" onClick={closeContactForm}>Cancel</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
    </div>
  );
}

export default SchoolForm;
